/* eslint-disable complexity */
import { useEffect, Dispatch, SetStateAction, useState } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import isEqual from 'lodash/isEqual'
import Cookies from 'js-cookie'
import { useUserRQInfo } from '../user-provider/user-provider'
import Link from '../Link'
import { GS_SEARCH_FILTER_QUERY_PARAM_NAME } from '../global-search-v2/use-global-search-query-params'
import { TDesktopDropDownOverlay } from '../HeaderV2/types/types'
import DesktopStocksNavigationV2 from '../HeaderV2/DesktopStocksNavigationV2'
import MobileNavSlider from '../HeaderV2/MobileNavSlider'
import { StencilCDNImage } from '../StencilImage'
import DesktopHeaderNavigation from './desktop-header-navigation'
import Logo from './logo'
import Hamburger from './hamburger'
import SearchIcon from './search-icon'
import SliderNav from './slide-nav'
import type { MobileOverlayStateType } from './utils/types'
import { HeaderRight } from './header-right'
import { eventData, eventMapping } from './header.tracking'
// import HelpIcon from '@/stories/assets/header/help.svg'
import useLockHTMLScroll from '@/hooks/useLockHTMLScroll'
import useMediaQuery from '@/hooks/useMediaQuery'
import { BETA_INDSTOCKS_HOST, INDSTOCKS_HOST } from '@/root/constants/common'
import RightArrow from '@/assets/common/arrow-right-inline.svg'
import { useAnalyticsEvent } from '@/hooks/analytics/use-analytics-event'
import { DASHBOARD_HEADER_NAV_CLICK } from '@/root/utils/common/web/gtm-events/eventsList'
import { CLOSE_SYMBOL } from '@/root/constants'
import PrettyBlueButton from '@/components/Stocks/TransactionSystem/PrettyBlueButton'
import Text from '@/stories/components/Common/NewText'

const IndicesNav = dynamic(
  () => import('@/components/TVChartContainer/IndicesNav'),
  {
    ssr: false,
  },
)

const MoreIcon = dynamic(() => import('./more-icon'), {
  ssr: false,
})

const GlobalSearchV2 = dynamic(
  () => import('../global-search-v2/global-search-v2'),
  {
    ssr: false,
  },
)

const DesktopNavigationV2 = dynamic(
  () => import('../HeaderV2/DesktopNavigationV2'),
  {
    ssr: true,
  },
)
const DropdownOverlay = dynamic(() => import('../HeaderV2/DropdownOverlay'), {
  ssr: true,
})

const FlashStocksHeader = dynamic(() => import('./flash-new-header'), {
  ssr: true,
})

// Add pathnames here where you want the POD navigation chips to be removed and the search bar width to be increased
export const disableNavigationChipsOnPathnamesArr = [
  '/stocks',
  '/futures-and-options',
  '/futures-and-options/options-chain/[slug]',
]

// eslint-disable-next-line max-lines-per-function
export function Header({
  isLargerThan1025,
  overlay,
  setOverlay,
  host,
  isInMobile,
}: {
  isLargerThan1025: boolean | null
  overlay: MobileOverlayStateType
  setOverlay: Dispatch<SetStateAction<MobileOverlayStateType>>
  host: string
  isInMobile?: boolean
}) {
  const router = useRouter()
  const { query, pathname } = router
  const pushEvent = useAnalyticsEvent()

  // const link =
  //   host === INDSTOCKS_HOST
  //     ? '/customer-service?topic_id=9&searchFilter=FAQ'
  //     : '/customer-service'

  const {
    userBasicRQData,
    userProfileRQData,
    isAuthenticated,
    isNotAuthenticated,
    isLoadingAuthenticated,
  } = useUserRQInfo()

  const memberId = query.memberId ?? Cookies.get('memberId')
  const firstName = memberId
      ? userProfileRQData.data?.familyMembers?.find(
          item => item?.id?.toString() == memberId,
        )?.name
      : userBasicRQData.data?.first_name,
    lastName = memberId ? '' : userBasicRQData.data?.last_name

  const initOverlayValue: MobileOverlayStateType =
    isLargerThan1025 === false &&
    router.query[GS_SEARCH_FILTER_QUERY_PARAM_NAME]
      ? 'search-overlay'
      : null

  const [dwebDropdown, toggleDwebDropdown] =
    useState<TDesktopDropDownOverlay>(null)
  const [mwebNavigation, toggleMwebNavigation] = useState<boolean>(false)

  useEffect(() => {
    if (memberId) {
      setOverlay(null)
    }
    if (initOverlayValue === 'search-overlay') {
      setOverlay(initOverlayValue)
    }
  }, [initOverlayValue, memberId, setOverlay, router.query])

  const toggleOverlay = (toggleOn: Exclude<MobileOverlayStateType, null>) => {
    return () => {
      setOverlay(prevState => {
        if (isEqual(prevState, toggleOn)) {
          return null
        } else if (
          typeof prevState === 'object' &&
          prevState?.type === 'logged-in-watchlist-overlay'
        ) {
          return null
        } else {
          return toggleOn
        }
      })
    }
  }

  const toggleSearchOverlay = toggleOverlay('search-overlay')
  const toggleMoreOverlay = toggleOverlay('more-overlay')
  // const toggleNavOverlay = toggleOverlay('nav-overlay')
  const toggleLoggedInSidebarOverlay = toggleOverlay(
    'logged-in-sidebar-overlay',
  )
  const toggleWatchlistOverlay = toggleOverlay({
    type: 'logged-in-watchlist-overlay',
    params: {
      mode: 'dashboard',
    },
  })

  const handleNavToggleWithEvents = e => {
    e.stopPropagation()
    let gtm4Payload
    if (mwebNavigation) {
      gtm4Payload = {
        eventData: eventMapping[eventData.headerCrossButtonClick],
      }
    } else {
      gtm4Payload = {
        eventData: eventMapping[eventData.headerHamburgerClick],
      }
    }
    pushEvent({ gtm4Payload })
    toggleMwebNavigation(!mwebNavigation)
  }

  useLockHTMLScroll({
    stateOnWhichLockToggles: overlay,
  })

  const isStocksFunnel = host === INDSTOCKS_HOST || host === BETA_INDSTOCKS_HOST
  const isTechnicalChart = pathname.includes('/technicalchart')

  // Derived visibility flags for cleaner JSX conditionals
  const isDesktop = !isInMobile
  const isLoggedOutReady = isNotAuthenticated && !isLoadingAuthenticated

  const showDesktopAuthNav = isDesktop && isAuthenticated
  const showDesktopLoggedOutNav =
    isLoggedOutReady && isDesktop && !isTechnicalChart && !isStocksFunnel
  const showDesktopStocksNav = isDesktop && isLoggedOutReady && isStocksFunnel
  const showDesktopHeaderRight = isLoggedOutReady && isDesktop
  const showDesktopDropdownOverlay =
    isDesktop && isLoggedOutReady && !isStocksFunnel
  const isStocksHomePage =
    isStocksFunnel && isLoggedOutReady && pathname === '/'

  return (
    <header
      className={classNames(
        'sticky left-0 top-0 z-50',
        'flex h-full w-full items-center border-b-1 border-brand-line bg-brand-white',
        {
          '!border-none !bg-black': isStocksHomePage,
        },
      )}
      id="site-header"
    >
      {isStocksHomePage ? (
        <FlashStocksHeader isDesktop={isDesktop} />
      ) : (
        <>
          <div
            className={classNames(
              'herolike-wrapper flex items-center justify-between py-2',
              {
                '!w-full px-2': isTechnicalChart,
              },
            )}
          >
            <div className="flex items-center gap-x-4">
              <div className="flex items-center gap-x-2">
                <Logo
                  host={host}
                  toggleDwebDropdown={toggleDwebDropdown}
                  setOverlay={setOverlay}
                />
                {showDesktopLoggedOutNav && (
                  <Text appearance="subtitle1">INDmoney</Text>
                )}
              </div>
              {showDesktopAuthNav && (
                <div
                  className={classNames('hidden md:block', {
                    'md:!hidden': isTechnicalChart,
                  })}
                >
                  <DesktopHeaderNavigation host={host} />
                </div>
              )}

              {showDesktopLoggedOutNav && (
                <div className="md:block">
                  <DesktopNavigationV2
                    dwebDropdown={dwebDropdown}
                    toggleDwebDropdown={toggleDwebDropdown}
                  />
                </div>
              )}

              {showDesktopStocksNav && (
                <DesktopStocksNavigationV2 host={host} />
              )}
            </div>
            {isTechnicalChart && <IndicesNav />}

            <div className="flex items-center justify-end gap-x-6">
              <aside
                className={classNames(
                  'text-brand-green flex items-center space-x-3',
                  {
                    hidden: !isInMobile,
                  },
                )}
              >
                {isNotAuthenticated && (
                  <>
                    <div
                      onClick={() => {
                        toggleMwebNavigation(false)
                        void router.push('/login')
                      }}
                      title="Log In to INDmoney"
                    >
                      <PrettyBlueButton
                        borderColorPrimary="border-brand-green-dark"
                        backgroundColorSecondary="bg-brand-white"
                        textColorPrimary="text-brand-green-dark"
                        paddingClassNames="px-5 py-2 text-sm leading-5"
                        primary={false}
                        hover={false}
                        text="Login"
                        marginClassNames="m-0"
                      />
                    </div>
                    {/* <div
                  title="Help Center"
                  className="cursor-pointer"
                  onClick={() => {
                    pushEvent({
                      gtm4Payload: {
                        eventData:
                          eventMapping[eventData.headerHelpCenterIconClick],
                      },
                    })
                    setOverlay(null)
                    toggleMwebNavigation(false)
                    void router.push(link)
                  }}
                >
                  <HelpIcon className="aspect-square w-6" />
                </div> */}
                  </>
                )}

                {isAuthenticated && (
                  <>
                    <div
                      onClick={() => {
                        pushEvent({
                          gtm4Payload: {
                            eventData:
                              eventMapping[eventData.headerSearchClick],
                          },
                        })
                        toggleSearchOverlay()
                        toggleMwebNavigation(false)
                      }}
                    >
                      <SearchIcon />
                    </div>
                    <div onClick={toggleMoreOverlay}>
                      <MoreIcon />
                    </div>
                  </>
                )}

                {isNotAuthenticated && (
                  <div onClick={handleNavToggleWithEvents}>
                    {mwebNavigation ? (
                      <div className="text-right text-24px text-brand-grey-base">
                        {/* cross */}
                        {CLOSE_SYMBOL}
                      </div>
                    ) : (
                      <Hamburger />
                    )}
                  </div>
                )}
              </aside>
              {isLargerThan1025 && isAuthenticated && (
                <div className="hidden lg:block">
                  <GlobalSearchV2
                    host={host}
                    toggleDwebDropdown={toggleDwebDropdown}
                  />
                </div>
              )}
              {isAuthenticated && isStocksFunnel && (
                <div
                  onClick={() => {
                    pushEvent({
                      mixpanelEventPayload: {
                        eventName: DASHBOARD_HEADER_NAV_CLICK,
                        payload: {
                          cta_name: 'Watchlist',
                        },
                      },
                    })
                    toggleWatchlistOverlay()
                  }}
                >
                  <StencilCDNImage
                    src="https://indcdn.indmoney.com/public/images/watchlist_icon_top_nav.png"
                    width={25}
                    height={25}
                    className="cursor-pointer"
                  />
                </div>
              )}

              {isStocksFunnel && (
                <Link
                  href="https://www.indmoney.com"
                  className={classNames(
                    'hidden items-center justify-center !rounded-[70px] bg-brand-grey-background px-5 py-[10px] font-medium text-brand-blue-dark lg:flex',
                    'ml-2 cursor-pointer rounded-xl',
                  )}
                  target="_blank"
                  title="Log In to INDmoney"
                >
                  <p>INDmoney.com</p>
                  <RightArrow
                    width="20"
                    height="20"
                    className="mt-1 flex-shrink-0 text-brand-black"
                  />
                </Link>
              )}
              {showDesktopHeaderRight && (
                <HeaderRight
                  host={host}
                  toggleDwebDropdown={toggleDwebDropdown}
                />
              )}

              {/* show logged in sidebar hamburger toggler */}
              {isAuthenticated && (
                <Text
                  appearance="subtitle1"
                  onClick={() => {
                    pushEvent({
                      mixpanelEventPayload: {
                        eventName: DASHBOARD_HEADER_NAV_CLICK,
                        payload: {
                          cta_name: 'Sidebar',
                        },
                      },
                    })
                    toggleLoggedInSidebarOverlay()
                  }}
                  addClassName="bg-brand-grey-base flex h-8 max-h-8 min-h-8 min-w-8 w-8 cursor-pointer items-center justify-center rounded-full text-white"
                >
                  {firstName && <span>{firstName[0].toUpperCase()}</span>}
                  {lastName && <span>{lastName[0].toUpperCase()}</span>}
                  {!firstName && !lastName && <span>U</span>}
                </Text>
              )}
            </div>
            <SliderNav
              overlay={overlay}
              isAuthenticated={isAuthenticated}
              toggleLoggedInSidebarOverlay={toggleLoggedInSidebarOverlay}
              toggleSearchOverlay={toggleSearchOverlay}
              toggleMoreOverlay={toggleMoreOverlay}
              // toggleNavOverlay={toggleNavOverlay}
              toggleWatchlistOverlay={toggleWatchlistOverlay}
              host={host}
              // TODO: rename this prop before merging
              data={{
                firstName,
                lastName,
                isSubscribed: !!userBasicRQData.data?.is_subscribed,
                subscription_plan: userBasicRQData.data?.subscription_plan,
                profileData: userProfileRQData,
              }}
              // isInMobile={isInMobile}
              // setOverlay={setOverlay}
            />
            {isInMobile && (
              <MobileNavSlider
                host={host}
                isAuthenticated={isAuthenticated}
                mwebNavigation={mwebNavigation}
                toggleMwebNavigation={toggleMwebNavigation}
              />
            )}
          </div>
          {showDesktopDropdownOverlay && (
            <DropdownOverlay
              dwebDropdown={dwebDropdown}
              toggleDwebDropdown={toggleDwebDropdown}
            />
          )}
        </>
      )}
    </header>
  )
}

export default function HeaderWithMediaQuery({
  overlay,
  setOverlay,
  host,
  isInMobile,
}: {
  overlay: MobileOverlayStateType
  setOverlay: Dispatch<SetStateAction<MobileOverlayStateType>>
  host: string
  isInMobile?: boolean
}) {
  const isLargerThan1025 = useMediaQuery({ breakpointToken: 'lg' })

  return (
    <Header
      isLargerThan1025={isLargerThan1025}
      overlay={overlay}
      setOverlay={setOverlay}
      key={isLargerThan1025?.toString()}
      host={host}
      isInMobile={isInMobile}
    />
  )
}
